/**
 * MuxPosterBackdrop — friendly progressive "blur-up" backdrop for a Mux video.
 *
 * Why this exists: a single full-size Mux thumbnail set as a CSS background
 * makes the user stare at a flat black screen + spinner until the whole
 * ~30-80 KB WebP downloads (painful on 3G / Instagram in-app browser).
 *
 * Strategy (industry-standard blur-up):
 *   1. Tasteful dark gradient base   → never a raw black flash, paints instantly.
 *   2. LQIP (≈40 px, q30, ~1-2 KB)   → blurred via CSS, lands almost immediately,
 *                                       gives instant colour + composition context.
 *   3. Full poster (device-sized)    → fades in over the LQIP once decoded.
 *   4. onReady() fires when the full  → lets the parent retire its spinner as soon
 *      poster is painted                as there is a real frame on screen.
 *
 * The LQIP <link rel="preload"> is injected as soon as the playbackId is known so
 * the browser fetches it in parallel with the JS that mounts the player. No
 * crossOrigin is set (image.mux.com doesn't need it; setting it breaks the hint).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export interface MuxPosterBackdropProps {
  /** Mux playback ID. When empty/undefined only the gradient base renders. */
  playbackId?: string | null;
  /** Frame time (seconds) for the thumbnail. Default 0 (first frame). */
  time?: number;
  /** Mux image fit mode. "smartcrop" fills (reels); "preserve" letterboxes. */
  fitMode?: "smartcrop" | "preserve";
  /** Fired once the full-resolution poster has decoded and is visible. */
  onReady?: () => void;
  /** Hide everything (e.g. when the video is already playing) with a fade. */
  hidden?: boolean;
  className?: string;
}

/**
 * Round viewport width to a CDN-cache-friendly bucket (avoids per-px cache misses).
 *
 * `smartcrop` on Mux Image fails (HTTP 400) when `width` exceeds the source
 * frame width. Vertical teasers are often ≤1080px wide (this brand's teaser is
 * 1072×1920), so high-DPR phones that would request 1080/1440 get a broken
 * poster and only see the blurry LQIP. Cap smartcrop requests at 720 — sharp
 * enough on mobile, always valid for typical reel assets. `preserve` can go higher.
 */
function posterWidthBucket(fitMode: "smartcrop" | "preserve"): number {
  if (typeof window === "undefined") {
    return fitMode === "smartcrop" ? 720 : 1080;
  }
  const w = window.innerWidth * (window.devicePixelRatio || 1);
  if (fitMode === "smartcrop") {
    if (w <= 480) return 480;
    return 720;
  }
  if (w <= 480) return 480;
  if (w <= 720) return 720;
  if (w <= 1080) return 1080;
  return 1440;
}

function buildThumbnailUrl(
  pid: string,
  time: number,
  width: number,
  quality: number,
  fitMode: "smartcrop" | "preserve",
): string {
  return `https://image.mux.com/${pid}/thumbnail.webp?time=${time}&width=${width}&quality=${quality}&fit_mode=${fitMode}`;
}

export default function MuxPosterBackdrop({
  playbackId,
  time = 0,
  fitMode = "smartcrop",
  onReady,
  hidden = false,
  className,
}: MuxPosterBackdropProps) {
  const pid = playbackId?.trim() || "";

  const lqipUrl = useMemo(
    () =>
      pid ? buildThumbnailUrl(pid, time, 40, 30, fitMode) : null,
    [pid, time, fitMode],
  );

  const preferredFullUrl = useMemo(
    () =>
      pid
        ? buildThumbnailUrl(
            pid,
            time,
            posterWidthBucket(fitMode),
            72,
            fitMode,
          )
        : null,
    [pid, time, fitMode],
  );

  /** Active full poster URL — may fall back to `preserve` if smartcrop 400s. */
  const [fullUrl, setFullUrl] = useState<string | null>(preferredFullUrl);
  const [fullLoaded, setFullLoaded] = useState(false);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    setFullUrl(preferredFullUrl);
    setFullLoaded(false);
  }, [preferredFullUrl]);

  // Preload the LQIP at high priority the moment we know the playback ID.
  useEffect(() => {
    if (!lqipUrl || typeof document === "undefined") return;
    const preconnect = (href: string) => {
      if (document.querySelector(`link[rel="preconnect"][href="${href}"]`)) return;
      const link = document.createElement("link");
      link.rel = "preconnect";
      link.href = href;
      link.crossOrigin = "anonymous";
      document.head.appendChild(link);
    };
    preconnect("https://image.mux.com");
    preconnect("https://stream.mux.com");

    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = lqipUrl;
    link.setAttribute("fetchpriority", "high");
    document.head.appendChild(link);
    return () => {
      link.remove();
    };
  }, [lqipUrl]);

  // Decode the full poster off-DOM; flip to "loaded" only once it can paint
  // without jank. If smartcrop 400s, retry once with preserve fit_mode.
  useEffect(() => {
    if (!fullUrl || !pid) return;
    let cancelled = false;
    const img = new Image();
    img.src = fullUrl;
    const markReady = () => {
      if (cancelled) return;
      setFullLoaded(true);
      onReadyRef.current?.();
    };
    const onFail = () => {
      if (cancelled) return;
      // smartcrop over-width → 400: fall back to preserve at same bucket.
      if (fitMode === "smartcrop" && fullUrl.includes("fit_mode=smartcrop")) {
        const fallback = buildThumbnailUrl(
          pid,
          time,
          posterWidthBucket("preserve"),
          72,
          "preserve",
        );
        if (fallback !== fullUrl) {
          setFullUrl(fallback);
          return;
        }
      }
      // Still paint the page (LQIP / gradient) rather than hang the spinner.
      markReady();
    };
    if (typeof img.decode === "function") {
      img.decode().then(markReady, onFail);
    } else {
      img.onload = markReady;
      img.onerror = onFail;
    }
    return () => {
      cancelled = true;
    };
  }, [fullUrl, pid, time, fitMode]);

  return (
    <div
      aria-hidden
      className={cn(
        "absolute inset-0 overflow-hidden transition-opacity duration-500 ease-out pointer-events-none",
        // visibility:hidden once faded out so a stuck poster never masks the
        // mux-player surface (paired with controller-visible fix in Mux player).
        hidden ? "opacity-0 invisible" : "opacity-100 visible",
        className,
      )}
    >
      {/* 1. Instant base — a calm dark gradient, never a raw black flash. */}
      <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-black to-purple-950/40" />

      {/* 2. LQIP — blurred, slightly over-scaled so blur edges never show. */}
      {lqipUrl && (
        <img
          src={lqipUrl}
          alt=""
          decoding="async"
          fetchPriority="high"
          className="absolute inset-0 h-full w-full object-cover scale-110 blur-2xl"
          style={{ willChange: "opacity" }}
        />
      )}

      {/* 3. Full poster — fades in over the LQIP once decoded. */}
      {fullUrl && (
        <img
          src={fullUrl}
          alt=""
          decoding="async"
          className={cn(
            "absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out",
            fullLoaded ? "opacity-100" : "opacity-0",
          )}
        />
      )}
    </div>
  );
}
